import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Home, Ruler, Wrench, Phone, Mail, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';
import useGooglePlacesAutocomplete from '../hooks/useGooglePlaces';
import { getUserProfile } from '../utils/userProfile';
import { PrivacyConsent } from '../components/ConsentCheckbox';
import Footer from '../components/sections/Footer';

const FreeEstimate = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isZh = i18n.language === 'zh';
  const [step, setStep] = useState(0);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const addressRef = useRef(null);

  // Login gate + profile prefill
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      toast.error(isZh ? '请登录系统' : 'Please login first');
      navigate('/profile', { state: { returnTo: '/estimate' } });
      return;
    }
    const loadProfile = async () => {
      const prof = await getUserProfile();
      if (prof) {
        setForm(f => ({
          ...f,
          name: f.name || prof.name || '',
          email: f.email || prof.email || '',
          phone: f.phone || prof.phone || '',
          address: f.address || prof.address || '',
        }));
      }
    };
    loadProfile();
  }, []);

  const [form, setForm] = useState({
    adu_type: '', sqft_range: '', reason: '', timeline: '', name: '', email: '', phone: '', address: ''
  });

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  useGooglePlacesAutocomplete(addressRef, {
    componentRestrictions: { country: 'us' },
    onPlaceSelect: (data) => {
      set('address', data.formatted_address || data.street_address);
    }
  });

  const sqftToConfig = (range) => {
    const map = {
      '400-600': { sqft: 550, beds: 1, baths: 1 },
      '600-800': { sqft: 700, beds: 2, baths: 1 },
      '800-1000': { sqft: 900, beds: 2, baths: 2 },
      '1000-1200': { sqft: 1100, beds: 3, baths: 2 },
      '1200+': { sqft: 1200, beds: 3, baths: 2 }
    };
    return map[range] || { sqft: 800, beds: 2, baths: 1 };
  };

  const steps = [
    { title: isZh ? 'ADU 类型' : 'ADU Type', field: 'adu_type', options: [
      { val: 'ground_up', label: isZh ? '全新建造' : 'Ground Up', icon: Home },
      { val: 'garage', label: isZh ? '车库改造' : 'Garage Conversion', icon: Wrench },
      { val: 'addition', label: isZh ? '房屋扩建' : 'Room Addition', icon: Ruler },
      { val: 'multiple', label: isZh ? '多个 ADU' : 'Multiple ADUs', icon: Home }
    ]},
    { title: isZh ? '面积需求' : 'Square Footage', field: 'sqft_range', options: [
      { val: '400-600', label: '400 - 600 sq ft' }, { val: '600-800', label: '600 - 800 sq ft' },
      { val: '800-1000', label: '800 - 1,000 sq ft' }, { val: '1000-1200', label: '1,000 - 1,200 sq ft' }, { val: '1200+', label: '1,200+ sq ft' }
    ]},
    { title: isZh ? '建造目的' : 'Purpose', field: 'reason', options: [
      { val: 'family', label: isZh ? '家庭扩展空间' : 'Family Space' },
      { val: 'rental', label: isZh ? '出租创收' : 'Rental Income' },
      { val: 'office', label: isZh ? '居家办公' : 'Home Office' },
      { val: 'aging', label: isZh ? '父母养老' : 'Age In Place' },
      { val: 'other', label: isZh ? '其他' : 'Other' }
    ]},
    { title: isZh ? '开始时间' : 'Timeline', field: 'timeline', options: [
      { val: 'asap', label: isZh ? '尽快开始' : 'As Soon As Possible' },
      { val: '3months', label: isZh ? '3个月内' : 'Next 3 Months' },
      { val: '6months', label: isZh ? '6个月内' : 'Next 6 Months' },
      { val: '6plus', label: isZh ? '6个月以上' : '6+ Months' }
    ]},
    { title: isZh ? '联系方式' : 'Contact Info', field: 'contact' }
  ];

  const handleSubmit = () => {
    if (!privacyAgreed) {
      toast.error(isZh ? '请勾选隐私声明' : 'Please agree to the privacy statement');
      return;
    }
    if (!form.name || !form.phone) {
      toast.error(isZh ? '请填写姓名和电话' : 'Please fill in name and phone');
      return;
    }
    const config = sqftToConfig(form.sqft_range);
    navigate(isZh ? '/zh/quote' : '/quote', {
      state: {
        property_address: form.address,
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        square_footage: config.sqft,
        bedrooms: config.beds,
        bathrooms: config.baths,
        notes: `Type: ${form.adu_type}, Reason: ${form.reason}, Timeline: ${form.timeline}`
      }
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white ">
      {/* Hero */}
      <section className="bg-red-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">{isZh ? '开始您的免费估价' : 'Begin your FREE estimate'}</h1>
          <p className="text-lg text-red-100">{isZh ? '填写项目需求后，进入详细报价系统生成完整报价单。' : 'Fill in your project needs, then generate a detailed quote with full pricing.'}</p>
        </div>
      </section>

      {/* Progress */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="flex gap-1 mb-8">
          {steps.map((_, i) => (
            <div key={i} className={`flex-1 h-2 rounded-full transition-all ${i <= step ? 'bg-red-600' : 'bg-gray-200'}`} />
          ))}
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-6">{steps[step].title}</h2>

        {step < 4 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
            {steps[step].options.map(opt => {
              const Icon = opt.icon;
              const active = form[steps[step].field] === opt.val;
              return (
                <button key={opt.val} onClick={() => { set(steps[step].field, opt.val); setTimeout(() => setStep(s => Math.min(s + 1, steps.length - 1)), 300); }}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${active ? 'border-red-600 bg-red-50' : 'border-gray-200 hover:border-red-300'}`}>
                  {Icon && <Icon className={`w-6 h-6 mb-2 ${active ? 'text-red-600' : 'text-gray-400'}`} />}
                  <span className="font-medium text-gray-900">{opt.label}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="space-y-4 mb-8">
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder={isZh ? '姓名 *' : 'Full Name *'} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-red-600 outline-none" data-testid="estimate-name" />
            <input value={form.email} onChange={e => set('email', e.target.value)} placeholder={isZh ? '邮箱' : 'Email'} type="email" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-red-600 outline-none" data-testid="estimate-email" />
            <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder={isZh ? '电话 *' : 'Phone *'} type="tel" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-red-600 outline-none" data-testid="estimate-phone" />
          </div>
        )}

        {step === steps.length - 1 && (
          <div className="mt-4">
            <PrivacyConsent agreed={privacyAgreed} setAgreed={setPrivacyAgreed} />
          </div>
        )}

        <div className="flex justify-between items-center mt-4">
          <Button variant="outline" onClick={() => setStep(s => Math.max(s - 1, 0))} disabled={step === 0} className="border-gray-300">
            {isZh ? '上一步' : 'Back'}
          </Button>
          {step < steps.length - 1 ? (
            <Button onClick={() => setStep(s => s + 1)} className="bg-red-600 hover:bg-red-700 text-white">
              {isZh ? '下一步' : 'Next'}
            </Button>
          ) : (
            <Button onClick={handleSubmit} className="bg-red-600 hover:bg-red-700 text-white px-8" data-testid="submit-estimate">
              <Send className="w-4 h-4 mr-2" /> {isZh ? '生成详细报价' : 'Generate Detailed Quote'}
            </Button>
          )}
        </div>
      </div>

      {/* Contact Info */}
      <section className="bg-gray-50 py-12 mt-12">
        <div className="max-w-4xl mx-auto px-6 grid md:grid-cols-3 gap-6 text-center">
          <div className="p-6">
            <Phone className="w-8 h-8 text-red-600 mx-auto mb-4" />
            <div className="space-y-1 text-center">
              {[{l:'SoCal:',n:'(626)803-8000',t:'6268038000'},{l:'NorCal:',n:'(408)900-6688',t:'4089006688'},{l:'San Diego:',n:'(858)910-0000',t:'8589100000'}].map(p=>(
                <a key={p.l} href={`tel:${p.t}`} className="block hover:text-red-600"><span className="text-gray-400 text-sm inline-block w-20 text-right">{p.l}</span> <span className="font-bold">{p.n}</span></a>
              ))}
            </div>
          </div>
          <div className="p-6"><Mail className="w-8 h-8 text-red-600 mx-auto mb-4" /><p className="font-bold">office@aligcus.com</p><p className="text-sm text-gray-500">{isZh ? '24小时内回复' : 'Response within 24hrs'}</p></div>
          <div className="p-6">
            <MapPin className="w-8 h-8 text-red-600 mx-auto mb-4" />
            <p className="font-bold text-sm">SoCal:</p>
            <p className="text-sm text-gray-500">1841 S San Gabriel Blvd, Suite D San Gabriel, CA 91776</p>
            <p className="font-bold text-sm mt-2">NorCal:</p>
            <p className="text-sm text-gray-500">1500 Wyatt Dr STE 12 Santa Clara, CA 95054</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FreeEstimate;
